import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Tag } from 'lucide-react';
import toast from 'react-hot-toast';

const ScanTagPage: React.FC = () => {
  const navigate = useNavigate();
  const [tagCode, setTagCode] = useState('');

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault(); // Prevent form reload
    }
    
    const trimmedTag = tagCode.trim();
    if (!trimmedTag) {
      toast.error('Please enter a tag code');
      return;
    }

    navigate(`/scan-upc/${encodeURIComponent(trimmedTag)}`);
    toast.success(`Tag "${trimmedTag}" submitted!`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Main Card */}
        <div className="bg-white shadow-xl rounded-2xl p-12 mb-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 text-gray-500 hover:text-gray-700 rounded-full p-3 hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <Tag className="h-8 w-8 text-blue-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-800">Scan Tag</h1>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8 text-center">
            <p className="text-gray-600 text-lg">
              Enter or scan a tag code 
              to begin the inventory process
            </p>
          </div>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label htmlFor="tagCode" className="block text-base font-semibold text-gray-700 mb-3">
                Tag Code
              </label>
              <div className="relative">
                <input
                  id="tagCode"
                  type="text"
                  value={tagCode}
                  onChange={(e) => setTagCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter or scan tag code..."
                  autoFocus
                  className="w-full px-6 py-4 pl-14 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-xl"
                />
                <Scan className="absolute left-5 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              </div>
            </div>

            <button
              type="submit"
              disabled={!tagCode.trim()}
              className={`w-full py-4 px-8 rounded-xl text-white font-semibold text-xl transition-all transform ${
                !tagCode.trim()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-105 shadow-lg hover:shadow-xl'
              }`}
            >
              {!tagCode.trim() ? 'Enter Tag Code' : 'Continue to UPC Scan'}
            </button>
          </form>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-base">
            💡 Tip: You can also use a barcode scanner to input the tag code
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScanTagPage;